import React, { useCallback, useEffect, useState } from "react";
import {
  append,
  find,
  length,
  lensPath,
  lensProp,
  mergeLeft,
  move,
  not,
  over,
  propEq,
  remove,
} from "ramda";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";

import DialogContent from "@material-ui/core/DialogContent";
import { set } from "ramda";
import Grid from "@material-ui/core/Grid";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/styles";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import { List, ListItem, ListItemText } from "@material-ui/core";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import Typography from "@material-ui/core/Typography";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.8rem",
  },
}));

const CustomDropdownDialog = ({
  project,
  fieldOptions,
  open,
  onCancel,
  onApply,
}) => {
  const classes = useStyles();
  const [fields, setFields] = useState({
    customDropdownLabel: null,
    customDropdownRequired: false,
  });
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (notBlank(fieldOptions["custom_dropdown"])) {
      setFields({
        customDropdownLabel: fieldOptions["custom_dropdown"].fieldLabel || null,
        customDropdownRequired:
          fieldOptions["custom_dropdown"].required || false,
      });
    }
    if (notBlank(project.customDropdownItems)) {
      setOptions(project.customDropdownItems);
    }
  }, [fieldOptions, project.customDropdownItems]);

  const [optionValue, setOptionValue] = useState(null);
  const [error, setError] = useState(null);

  const handleAddOptionClick = useCallback(() => {
    const existing = find(propEq("customDropdownName", optionValue), options);
    if (notBlank(existing)) {
      setError(
        "A Dropdown Item Option with the specified value already exists.",
      );
    } else {
      setOptions(prev =>
        mapIndexed(
          (val, idx) => set(lensProp("displayOrder"), idx, val),
          append({ customDropdownName: optionValue, active: true }, prev),
        ),
      );
      setOptionValue("");
      setError(null);
    }
  }, [optionValue, options]);

  const handleTextChange = path => event => {
    const value = event.target.value;
    setFields(prev => set(lensPath(path), value, prev));
  };

  const handleCheckboxChange = field => event => {
    const value = event.target.checked;
    setFields(prev => set(lensProp(field), value, prev));
  };

  const handleToggleVisible = idx => {
    setOptions(prev => {
      return over(lensPath([idx, "active"]), not, prev);
    });
  };

  const handleDeleteOption = idx => {
    setOptions(prev =>
      mapIndexed(
        (val, idx) => set(lensProp("displayOrder"), idx, val),
        remove(idx, 1, prev),
      ),
    );
  };

  const handleOptionUp = idx => {
    setOptions(prev =>
      mapIndexed(
        (val, idx) => set(lensProp("displayOrder"), idx, val),
        move(idx, idx - 1, prev),
      ),
    );
  };

  const handleOptionDown = idx => {
    setOptions(prev =>
      mapIndexed(
        (val, idx) => set(lensProp("displayOrder"), idx, val),
        move(idx, idx + 1, prev),
      ),
    );
  };

  const handleApply = async () => {
    onApply(mergeLeft(fields, { customDropdownItems: options }));
  };

  return (
    <Dialog open={open}>
      <DialogTitle id="form-dialog-title">
        Edit Custom Dropdown Items
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Enter properties for the Custom Dropdown field.
        </DialogContentText>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12}>
            <TextField
              id="custom-dropdown-label"
              fullWidth
              label="Custom Dropdown Label"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.customDropdownLabel}
              onChange={handleTextChange(["customDropdownLabel"])}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.customDropdownRequired}
                  onChange={handleCheckboxChange("customDropdownRequired")}
                  name="custom-dropdown-required"
                  inputProps={{ "aria-label": "Select" }}
                />
              }
              label="Custom Dropdown Required"
            />
          </Grid>
          <Grid item md={12}>
            <Typography variant="h5">Options</Typography>
            <List dense>
              {mapIndexed(
                (opt, idx) => (
                  <ListItem
                    divider
                    key={opt.customDropdownId || opt.customDropdownName}
                  >
                    <ListItemText primary={opt.customDropdownName} />
                    <ListItemSecondaryAction>
                      {isBlank(opt.customDropdownId) && (
                        <ConfirmButton
                          handleDelete={() => handleDeleteOption(idx)}
                          dialogTitle="Remove Custom Dropdown"
                          dialogBody="Are you sure you want to remove this custom dropdown?"
                        />
                      )}

                      {opt.active ? (
                        <Tooltip title="Mark Hidden">
                          <IconButton
                            onClick={() => handleToggleVisible(idx)}
                            edge="end"
                            aria-label="hide"
                          >
                            <VisibilityIcon color="primary" fontSize="large" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Mark Visible">
                          <IconButton
                            onClick={() => handleToggleVisible(idx)}
                            edge="end"
                            aria-label="show"
                          >
                            <VisibilityOffIcon color="error" fontSize="large" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <IconButton
                        onClick={() => handleOptionUp(idx)}
                        disabled={idx === 0}
                        edge="end"
                        aria-label="move up"
                      >
                        <Tooltip title="Move Up">
                          <ArrowDropUpIcon
                            color={idx === 0 ? "disabled" : "primary"}
                            fontSize="large"
                          />
                        </Tooltip>
                      </IconButton>
                      <IconButton
                        onClick={() => handleOptionDown(idx)}
                        disabled={idx === length(options) - 1}
                        edge="end"
                        aria-label="move down"
                      >
                        <Tooltip title="Move Down">
                          <ArrowDropDownIcon
                            color={
                              idx === length(options) - 1
                                ? "disabled"
                                : "primary"
                            }
                            fontSize="large"
                          />
                        </Tooltip>
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ),
                options,
              )}
            </List>
          </Grid>
          <Grid item md={8}>
            <TextField
              id="option-value"
              fullWidth
              label="New Custom Download Option"
              InputProps={{
                style: { fontSize: "1.8rem" },
              }}
              value={optionValue}
              onChange={event => setOptionValue(event.target.value)}
              error={notBlank(error)}
              helperText={error}
            />
          </Grid>
          <Grid item md={3}>
            <Button
              variant="outlined"
              onClick={handleAddOptionClick}
              disabled={isBlank(optionValue)}
              aria-label="add option"
            >
              Add Option
            </Button>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomDropdownDialog;
